import React, { useCallback, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import DataGrid, {
	Column,
	Paging,
	Pager,
	FilterRow,
	HeaderFilter,
	FilterPanel,
	Sorting,
	Selection,
	Export,
} from 'devextreme-react/data-grid';
import { exportDataGrid as dxExportPdfFn } from 'devextreme/pdf_exporter';
import { exportDataGrid as dxExportExcelFn } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import autoTable from 'jspdf-autotable';
import { createUUID } from '../../../utils/createUUID';
import { dxFont } from './dxtable-control/dxFont';
import { Button } from '../../base';
import { DxToolbar, DxToolbarItem } from './dxtable-control/DxTableControl';

export type IDxTableSelectionProps = {
	mode: 'multiple' | 'single';
	onSelectionChanged: (e: any) => void;
	selectedRowKeys?: any[];
};

export type IDxTableFilterProps = {
	refreshDataGrid: () => void;
};

export type IDxTableProps = {
	dataSource: any[];
	keyExpr: string;
	selection?: IDxTableSelectionProps;
	filters?: IDxTableFilterProps;
	toolbars?: any;
	columns?: any;
	isLoading?: boolean;
	isShowIdx?: boolean;
	defaultPageSize?: number;
	showFilterPanel?: boolean;
	children?: React.ReactNode;
	wordWrapEnabled?: boolean;
	isExport?: boolean;
	exportTitle?: string;
	height?: any;
	width?: any;
	isHeaderFilter?: boolean;
	hasFixed?: boolean;
};

const DxTable = React.memo((props: IDxTableProps) => {
	const {
		dataSource,
		keyExpr,
		columns,
		isShowIdx,
		isLoading,
		defaultPageSize,
		showFilterPanel,
		children,
		selection,
		wordWrapEnabled,
		exportTitle,
		height,
		width,
		toolbars,
		filters,
		isHeaderFilter,
		isExport,
		hasFixed,
	} = props;
	const [isDataLoading] = useDebounce<boolean>(isLoading || false, 800);

	const onExporting = React.useCallback(
		(e: any) => {
			if (e?.format === 'pdf') {
				const doc = new jsPDF({
					orientation: 'landscape',
					unit: 'mm',
					format: [210, 297],
				});
				doc.addFileToVFS('NotoSerif-Regular-normal.ttf', dxFont.font);
				doc.addFont('NotoSerif-Regular-normal.ttf', 'NotoSerif-Regular', 'normal');
				doc.setFont('NotoSerif-Regular');
				autoTable(doc, {
					theme: 'grid',
					margin: { horizontal: 7 },
					bodyStyles: { valign: 'top' },
					styles: { overflow: 'linebreak', cellWidth: 'auto' },
				});
				dxExportPdfFn({
					jsPDFDocument: doc,
					component: e.component,
					indent: 2,
				}).then(() => {
					doc.save(`${exportTitle || 'dxTableDataGrd'}.pdf`);
				});
			}

			if (e?.format === 'xlsx') {
				const workbook = new Workbook();
				const worksheet = workbook.addWorksheet('DATA');
				dxExportExcelFn({
					component: e.component,
					worksheet,
					autoFilterEnabled: true,
				}).then(() => {
					workbook.xlsx.writeBuffer().then((buffer) => {
						saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${exportTitle || 'dxTableDataGrd'}.xlsx`);
					});
				});
			}
			e.cancel = true;
		},
		[exportTitle]
	);

	return (
		<>
			<DataGrid
				dataSource={dataSource}
				keyExpr={keyExpr || 'id'}
				showBorders={true}
				columnAutoWidth={true}
				showColumnLines={true}
				showRowLines={true}
				allowColumnResizing={true}
				columnResizingMode={'nextColumn'}
				hoverStateEnabled={true}
				className='table-noborder-left'
				noDataText={isDataLoading ? 'Đang tải dữ liệu ...' : 'Không có dữ liệu'}
				onSelectionChanged={selection ? selection.onSelectionChanged : () => {}}
				selectedRowKeys={selection && selection.selectedRowKeys ? selection.selectedRowKeys : []}
				wordWrapEnabled={wordWrapEnabled || false}
				height={height || window.innerHeight - 200}
				width={width}
				loadPanel={{ enabled: true }}
				allowColumnReordering={true}
				rowAlternationEnabled={true}
				onExporting={(e) => onExporting(e)}
			>
				<Sorting mode='multiple' />
				<HeaderFilter visible={isHeaderFilter || false} />
				<FilterRow visible={true} />
				<Export
					enabled={isExport && isExport === true}
					allowExportSelectedData={true}
					texts={{ exportAll: 'Xuất {0}', exportSelectedRows: 'Xuất {0} những dòng đã chọn', exportTo: 'Xuất dữ liệu' }}
					formats={['xlsx', 'pdf']}
				/>
				{showFilterPanel === true && <FilterPanel visible={true} />}

				<Paging enabled={(defaultPageSize || 0) > 0} defaultPageSize={defaultPageSize || 50} />
				{defaultPageSize && (
					<Pager
						showPageSizeSelector={true}
						allowedPageSizes={[10, 20, 50, 100, 200, 500]}
						// infoText='Trang {0} /{1} ({2} dòng)'
						// showInfo={true}
					/>
				)}
				{(isShowIdx === true || isShowIdx === undefined) && (
					<Column
						visibleIndex={0}
						caption={'#'}
						cellRender={(cell: any) => {
							return <span>{cell.rowIndex + 1}</span>;
						}}
						width={40}
						alignment='center'
						allowExporting={false}
						fixed={hasFixed || false}
					/>
				)}
				{selection && <Selection mode={selection.mode} selectAllMode={'allPages'} showCheckBoxesMode={'always'} />}
				{selection && (
					<Column
						width={40}
						key={createUUID()}
						type={'selection'}
						visibleIndex={1}
						allowExporting={false}
						fixed={hasFixed || false}
					></Column>
				)}
				{columns ? columns : null}
				{children}
				<DxToolbar>
					{toolbars}
					{filters && (
						<DxToolbarItem location='after'>
							<Button text='Làm mới' icon='icon ni ni-reload-alt' onClick={() => filters.refreshDataGrid()} />
						</DxToolbarItem>
					)}
					<DxToolbarItem location='after' name={'exportButton'} />
				</DxToolbar>
			</DataGrid>
		</>
	);
});

export default DxTable;
